import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { environment } from '../../../environments/environment';
import { getMatchJokerCost } from '../../constants/match-round.constants';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { FinalResultService } from '../../services/final-result.service';
import { GroupsService } from '../../services/groups.service';
import { LocalService } from '../../services/local.service';
import { MatchesService } from '../../services/matches.service';
import { PollsService } from '../../services/polls.service';
import { SidenavService } from '../../services/sidenav.service';
import { StandingsService } from '../../services/standings.service';
import { UtilsService } from '../../services/utils.service';
import { UsersListModalComponent } from '../shared/users-list-modal/users-list-modal.component';

@Component({
  selector: 'app-standings',
  templateUrl: './standings.component.html',
  styleUrls: ['./standings.component.scss'],
  standalone: false,
})
export class StandingsComponent implements OnInit, OnDestroy {
  @BlockUI() blockUI: NgBlockUI;

  sidenav: MatSidenav;
  standings: Array<any>;
  groups: Array<any> = [];
  groupSelected: string;
  loggedUser: any;
  isAdmin: boolean;
  firstRoundIniTimestamp: number;
  currentTimestamp: number;
  liveMatches: Array<any>;
  pichichiNames: Record<string, string> = {};
  includeTemporaryPichichi = false;
  includeJokers = true;
  private finishedMatches: Array<any>;
  private polls: Array<any>;
  private matches: Array<any>;
  private finalResult: any;
  private allStandings: any;
  private readonly standingsFiltersStorageKey = 'standings-filters';
  private liveMatchesInterval?: ReturnType<typeof setInterval>;
  private liveScoreByMatchId = new Map<string, string>();

  constructor(
    private sidenavService: SidenavService,
    private authService: AuthService,
    private localService: LocalService,
    private standingsService: StandingsService,
    private groupsService: GroupsService,
    private matchesService: MatchesService,
    private router: Router,
    private pollsService: PollsService,
    private utilsService: UtilsService,
    private finalResultService: FinalResultService,
    private apiService: ApiService,
    private bottomSheet: MatBottomSheet,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
    this.standings = [];
    this.loggedUser = this.authService.getUser();
    this.isAdmin = this.authService.isAdmin();
  }

  async ngOnInit() {
    if (!this.loggedUser?._id) {
      this.router.navigate(['/login']);
      return;
    }

    this.blockUI.start();
    try {
      const [storedGroup, storedFilters] = await Promise.all([
        this.localService.getDataV2<string>('stgrsel'),
        this.localService.getDataV2<{
          includeTemporaryPichichi?: boolean;
          includeJokers?: boolean;
        }>(this.standingsFiltersStorageKey),
      ]);

      this.includeTemporaryPichichi =
        storedFilters?.includeTemporaryPichichi === true;
      this.includeJokers = storedFilters?.includeJokers !== false;

      const [
        groups,
        matches,
        rounds,
        finalResult,
        allStandings,
      ] = await Promise.all([
        this.groupsService.getList(),
        this.matchesService.getList(),
        this.matchesService.getRounds(),
        this.finalResultService.get(),
        this.standingsService.getSummary(
          this.includeTemporaryPichichi,
          this.includeJokers,
        ),
        this.resolvePichichiNames(),
      ]);

      this.groups = this.isAdmin
        ? groups
        : groups.filter((group: any) =>
            this.loggedUser.groups?.includes(group._id),
          );
      this.matches = matches;
      this.groupSelected = storedGroup || 'general';
      if (
        this.groupSelected !== 'general' &&
        !this.groups.some((group: any) => group._id === this.groupSelected)
      ) {
        this.groupSelected = 'general';
        await this.localService.saveDataV2('stgrsel', this.groupSelected);
      }
      this.firstRoundIniTimestamp = rounds[0].iniTimestamp;
      this.currentTimestamp = new Date().getTime() / 1000;
      this.finalResult = finalResult;
      this.allStandings = allStandings;
      await this.getLiveMatches();
      this.liveScoreByMatchId = this.getLiveScoreByMatchId();
      await this.showStandings();
      this.liveMatchesInterval = setInterval(
        () => void this.refreshLiveMatches().catch(() => undefined),
        60000,
      );
    } finally {
      this.blockUI.stop();
    }
  }

  ngOnDestroy(): void {
    if (this.liveMatchesInterval) {
      clearInterval(this.liveMatchesInterval);
    }
  }

  toggleMenu() {
    this.sidenavService.toggle();
  }

  selectUser(user: any) {
    this.router.navigate([`/home/${user._id}`]);
  }

  async selectGroup(groupId: string) {
    this.groupSelected = groupId;
    await this.localService.saveDataV2('stgrsel', this.groupSelected);
    await this.showStandings();
  }

  async reload() {
    this.blockUI.start();
    try {
      [this.matches, this.allStandings] = await Promise.all([
        this.matchesService.getList(),
        this.standingsService.getSummary(
          this.includeTemporaryPichichi,
          this.includeJokers,
        ),
      ]);
      await this.getLiveMatches();
      this.liveScoreByMatchId = this.getLiveScoreByMatchId();
      await this.showStandings();
    } finally {
      this.blockUI.stop();
    }
  }

  async toggleTemporaryPichichi() {
    const nextValue = !this.includeTemporaryPichichi;
    this.blockUI.start();
    try {
      this.allStandings = await this.standingsService.getSummary(
        nextValue,
        this.includeJokers,
      );
      this.includeTemporaryPichichi = nextValue;
      await this.saveStandingsFilters();
      await this.showStandings();
    } finally {
      this.blockUI.stop();
    }
  }

  async toggleJokers() {
    const nextValue = !this.includeJokers;
    this.blockUI.start();
    try {
      this.allStandings = await this.standingsService.getSummary(
        this.includeTemporaryPichichi,
        nextValue,
      );
      this.includeJokers = nextValue;
      await this.saveStandingsFilters();
      await this.showStandings();
    } finally {
      this.blockUI.stop();
    }
  }

  private saveStandingsFilters(): Promise<void> {
    return this.localService.saveDataV2(this.standingsFiltersStorageKey, {
      includeTemporaryPichichi: this.includeTemporaryPichichi,
      includeJokers: this.includeJokers,
    });
  }

  showUsers(event: { title: string; users: any[] }) {
    this.bottomSheet
      .open(UsersListModalComponent, {
        data: {
          title: event.title,
          users: event.users,
          groups: this.groups,
          positionByUserId: this.buildPositionByUserId(
            this.allStandings?.currentStandings ?? [],
          ),
        },
        panelClass: 'participants-sheet',
      })
      .afterDismissed()
      .subscribe((selectedUser) => {
        if (selectedUser?._id) {
          this.router.navigate([`/home/${selectedUser._id}`]);
        }
      });
  }

  private buildPositionByUserId(standings: any[]): Record<string, number> {
    return standings.reduce((positions, standing, index) => {
      if (standing?.user?._id) {
        positions[standing.user._id] = index + 1;
      }

      return positions;
    }, {});
  }

  private async showStandings() {
    const standings = { ...this.allStandings };
    let currentStandings: any[] = [];
    let evolution: any[] = [];

    if (this.groupSelected === 'general') {
      currentStandings = standings.currentStandings;
      evolution = standings.standingsEvolution || [];
    } else {
      currentStandings = standings.currentStandings.filter((item: any) =>
        item.user.groups?.includes(this.groupSelected),
      );
      evolution = (standings.standingsEvolution || []).map((journey: any[]) =>
        journey.filter((item: any) =>
          item.user.groups?.includes(this.groupSelected),
        ),
      );
    }

    // this.standings = standings.currentStandings;

    // const userClassifications = standings.standingsEvolution.filter(
    //   (element: any) => element.user._id === this.loggedUser._id
    // );
    const mappedStandings = currentStandings.map((standing: any) => {
      if (evolution.length < 2) {
        standing.posChange = {
          number: '',
          change: 'equal',
        };
      } else {
        const previousPosition = evolution[evolution.length - 2].findIndex(
          (item: any) => item.user._id === standing.user._id,
        );
        const currentPosition = evolution[evolution.length - 1].findIndex(
          (item: any) => item.user._id === standing.user._id,
        );
        const changedPos =
          previousPosition === -1 || currentPosition === -1
            ? 0
            : currentPosition - previousPosition;

        standing.posChange = {
          number: changedPos === 0 ? '' : Math.abs(changedPos),
          change: changedPos === 0 ? 'equal' : changedPos > 0 ? 'down' : 'up',
        };
      }

      standing.jokersConsumed = this.finishedMatches.reduce((acc, match) => {
        if (standing.user.jokers.matches.indexOf(match._id) !== -1) {
          return acc + getMatchJokerCost(match.round);
        } else {
          return acc + 0;
        }
      }, 0);

      standing.currentResults = this.liveMatches?.map((match) => {
        const userPoll = this.polls.find(
          (poll: any) => poll.user?._id === standing.user._id,
        );

        if (!userPoll) {
          return { homeGoals: 0, awayGoals: 0, useJoker: false };
        }

        const pollMatch = userPoll.results.find(
          (result: any) => result.match._id === match._id,
        );
        return {
          homeGoals: pollMatch?.homeGoals,
          awayGoals: pollMatch?.awayGoals,
          penaltiesWinner: pollMatch?.penaltiesWinner,
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          round: match.round,
          useJoker: userPoll.user.jokers?.matches?.indexOf(match._id) !== -1,
        };
      });

      standing.championFlag = this.getChampionFlag(standing.user);
      standing.championName = this.getChampionName(standing.user);
      standing.pichichiLabel = this.getPichichiLabel(standing.user.pichichi);

      return standing;
    });

    this.standings = mappedStandings;
  }

  private getChampionFlag(user: any): string | null {
    return user?.champion?.flag ?? user?.team?.flag ?? null;
  }

  private getChampionName(user: any): string {
    if (typeof user?.champion === 'string') {
      return user.champion;
    }

    return user?.champion?.name ?? user?.team?.name ?? 'Selección escogida';
  }

  private getPichichiLabel(pichichi: any): string | null {
    if (!pichichi) {
      return null;
    }

    if (typeof pichichi === 'string') {
      return this.pichichiNames[pichichi] ?? pichichi;
    }

    return (
      pichichi.name ?? this.pichichiNames[pichichi._id] ?? pichichi._id ?? null
    );
  }

  private async resolvePichichiNames(): Promise<void> {
    const players: any[] = await this.apiService
      .request('/players', 'GET')
      .catch(() => []);
    players.forEach((player: any) => {
      if (player?._id && player?.name) {
        this.pichichiNames[player._id] = player.name;
      }
    });
  }

  private async getLiveMatches() {
    this.finishedMatches =
      this.matches.filter((match: any) => match.status === 'FINISHED') ?? [];
    let liveMatches = this.matches.filter((match: any) => {
      return ['IN_PLAY', 'LIVE', 'PAUSED'].includes(match.status);
    });

    if (liveMatches.length === 0 && environment.mockLiveMatch) {
      liveMatches = this.buildMockLiveMatches();
    }

    if (liveMatches.length === 0) {
      this.liveMatches = [];
      return;
    }

    this.polls = await this.pollsService.getList();
    this.liveMatches = this.utilsService.addUIPollsPropertiesToMatches(
      liveMatches,
      this.polls,
      this.loggedUser,
    );
  }

  private buildMockLiveMatches(): any[] {
    const sourceMatches = this.matches.filter(
      (match: any) => match.homeTeam && match.awayTeam,
    );
    const firstMatch = sourceMatches[0];
    const secondMatch = sourceMatches[1] ?? sourceMatches[0];

    if (!firstMatch) {
      return [];
    }

    const mocks = [
      {
        ...firstMatch,
        _id: `${firstMatch._id}-mock-live`,
        status: 'IN_PLAY',
        homeGoals: 2,
        awayGoals: 1,
        minute: 67,
        injuryTime: 2,
        period: 'SECOND_HALF',
        isLiveMock: true,
      },
    ];

    if (secondMatch) {
      mocks.push({
        ...secondMatch,
        _id: `${secondMatch._id}-mock-penalties`,
        status: 'IN_PLAY',
        homeGoals: 1,
        awayGoals: 1,
        homeExtraTimeGoals: 0,
        awayExtraTimeGoals: 0,
        homePenalties: 2,
        awayPenalties: 3,
        minute: 120,
        injuryTime: null,
        period: 'PENALTIES',
        isLiveMock: true,
      });
    }

    return mocks;
  }

  private async refreshLiveMatches(): Promise<void> {
    this.matches = await this.matchesService.getList();
    const previousLiveScoreByMatchId = this.liveScoreByMatchId;
    await this.getLiveMatches();

    this.liveScoreByMatchId = this.getLiveScoreByMatchId();
    if (this.hasLiveScoreChanged(previousLiveScoreByMatchId)) {
      this.allStandings = await this.standingsService.getSummary(
        this.includeTemporaryPichichi,
        this.includeJokers,
      );
    }

    await this.showStandings();
  }

  private getLiveScoreByMatchId(): Map<string, string> {
    return new Map(
      (this.liveMatches ?? []).map((match: any) => [
        match._id,
        [
          match.homeGoals,
          match.awayGoals,
          match.homeExtraTimeGoals,
          match.awayExtraTimeGoals,
          match.homePenalties,
          match.awayPenalties,
          match.period,
        ]
          .map((value) => value ?? '')
          .join(':'),
      ]),
    );
  }

  private hasLiveScoreChanged(previousLiveScoreByMatchId: Map<string, string>) {
    if (previousLiveScoreByMatchId.size === 0) {
      return false;
    }

    return Array.from(this.liveScoreByMatchId.entries()).some(
      ([matchId, score]) =>
        previousLiveScoreByMatchId.has(matchId) &&
        previousLiveScoreByMatchId.get(matchId) !== score,
    );
  }

  getLivePredictionLabel(result: any): string {
    const score = `${result.homeGoals ?? ''}-${result.awayGoals ?? ''}`;

    if (
      result.round !== 'GROUP_STAGE' &&
      result.homeGoals === result.awayGoals &&
      result.penaltiesWinner
    ) {
      const winnerId = this.getWinnerId(result.penaltiesWinner);
      const winnerTeam =
        winnerId === result.homeTeam?._id ? result.homeTeam : result.awayTeam;
      return `${score} p. ${this.getTeamCode(winnerTeam)}`;
    }

    return score;
  }

  private getWinnerId(winner: any): string | null {
    if (!winner) return null;
    return typeof winner === 'string' ? winner : (winner._id ?? null);
  }

  getLiveStatusLabel(match: any): string {
    if (match.period === 'HALF_TIME') {
      return 'Descanso';
    }

    if (match.period === 'PENALTIES') {
      return 'Penaltis';
    }

    const periodLabels: Record<string, string> = {
      FIRST_HALF: '1.ª parte',
      SECOND_HALF: '2.ª parte',
      EXTRA_TIME: 'Prórroga',
    };
    const periodLabel = periodLabels[match.period] ?? 'En juego';
    const minute = Number.isFinite(match.minute) ? match.minute : null;
    const injuryTime =
      Number.isFinite(match.injuryTime) && match.injuryTime > 0
        ? `+${match.injuryTime}`
        : '';
    const minuteLabel = minute == null ? '' : `${minute}${injuryTime}′`;

    return [periodLabel, minuteLabel].filter(Boolean).join(' · ');
  }

  getLiveClockLabel(match: any): string {
    const periodLabels: Record<string, string> = {
      FIRST_HALF: '1.ª parte',
      HALF_TIME: 'Descanso',
      SECOND_HALF: '2.ª parte',
      EXTRA_TIME: 'Prórroga',
      PENALTIES: 'Penaltis',
    };

    if (periodLabels[match.period]) {
      return periodLabels[match.period];
    }

    return match.status === 'PAUSED' ? 'Descanso' : 'En juego';
  }

  getLivePhaseScoreLabel(match: any): string {
    if (match.period === 'PENALTIES') {
      return this.formatLivePhaseScore(
        'Penaltis',
        match.homePenalties,
        match.awayPenalties,
      );
    }

    if (match.period === 'EXTRA_TIME') {
      return this.formatLivePhaseScore(
        'Prórroga',
        match.homeExtraTimeGoals,
        match.awayExtraTimeGoals,
      );
    }

    return '';
  }

  private formatLivePhaseScore(
    label: string,
    homeGoals: any,
    awayGoals: any,
  ): string {
    if (!Number.isFinite(homeGoals) || !Number.isFinite(awayGoals)) {
      return '';
    }

    return `${label} ${homeGoals}-${awayGoals}`;
  }

  getTeamCode(team: any): string {
    const rawCode =
      team?.tla ?? team?.code ?? team?.shortName ?? team?.abbreviation;

    if (rawCode) {
      return String(rawCode).slice(0, 3).toUpperCase();
    }

    const normalizedName = String(team?.name ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    const teamCodes: Record<string, string> = {
      'corea del sur': 'KOR',
      croacia: 'CRO',
      england: 'ENG',
      inglaterra: 'ENG',
      'south africa': 'RSA',
      'south korea': 'KOR',
      sudafrica: 'RSA',
    };

    if (teamCodes[normalizedName]) {
      return teamCodes[normalizedName];
    }

    const words = normalizedName
      .split(/[^a-z]+/)
      .filter((word) => word && !['de', 'del', 'la', 'the'].includes(word));

    if (words.length > 1) {
      return words.map((word) => word[0]).join('').slice(0, 3).toUpperCase();
    }

    return normalizedName
      .replace(/[^a-z]/g, '')
      .slice(0, 3)
      .toUpperCase();
  }
}
